"use client";

import { useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  fullPool,
  parseNameLines,
  pickLines,
  removeLines,
} from "@/lib/tool-kit/random-tools/name-picker";

/** English strings. A translated content file overrides these through `ui`. */
export const RANDOM_NAME_PICKER_STRINGS = {
  namesOnePerLine: "Names — one per line",
  namesPlaceholder: "Alice\nBen\nChloe\nDiego",
  poolSummary:
    "one:{count} name entered · {pool} in the pool|other:{count} names entered · {pool} in the pool",
  options: "Options",
  namesToPick: "Names to pick",
  removePickedFromPool: "Remove picked names from the pool",
  pickNames: "one:Pick a Name|other:Pick {count} Names",
  resetPool: "Reset Pool",
  pickedAtRandom: "Picked at random",
  namesEntered: "Names entered",
  leftInPool: "Left in pool",
  pickedSoFar: "Picked so far",
  pickHistory: "Pick history",
} as const;

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

export default function RandomNamePickerWidget() {
  const locale = useToolLocale();
  const strings = useToolUi(RANDOM_NAME_PICKER_STRINGS);
  const [input, setInput] = useState("");
  const [pickCount, setPickCount] = useState(1);
  const [removePicked, setRemovePicked] = useState(false);
  // Line numbers still in play, or null while no pick has removed anything. Lines are
  // tracked by position so removing one pick never removes a same-text line.
  const [pool, setPool] = useState<number[] | null>(null);
  const [lastPicked, setLastPicked] = useState<string[]>([]);
  const [history, setHistory] = useState<string[]>([]);
  const [flash, setFlash] = useState(false);

  const allNames = parseNameLines(input);
  const activePool = pool ?? fullPool(allNames.length);

  function handleInputChange(value: string) {
    setInput(value);
    setPool(null);
    setLastPicked([]);
  }

  function handlePick() {
    if (activePool.length === 0) {
      return;
    }
    const pickedLines = pickLines(activePool, pickCount);
    const picked = pickedLines.map((line) => allNames[line] ?? "");
    setLastPicked(picked);
    setHistory((prev) => [...prev, ...picked]);
    if (removePicked) {
      setPool(removeLines(activePool, pickedLines));
    }
    setFlash(true);
    setTimeout(() => setFlash(false), 600);
  }

  function handleResetPool() {
    setPool(null);
    setLastPicked([]);
    setHistory([]);
  }

  const highlightClass = flash
    ? "border-primary bg-primary/10"
    : "border-border bg-card";

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="picker-input"
        >
          {strings.namesOnePerLine}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="picker-input"
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder={strings.namesPlaceholder}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {tPlural(strings.poolSummary, locale, allNames.length, {
            pool: activePool.length,
          })}
        </p>
      </div>

      {/* Options */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.options}</p>
        <div className="flex flex-wrap items-center gap-4">
          <label
            className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm"
            htmlFor="pick-count"
          >
            {strings.namesToPick}
            <input
              className="w-16 rounded border border-border bg-background px-2 py-1 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="pick-count"
              max={100}
              min={1}
              onChange={(e) =>
                setPickCount(
                  Math.max(1, parseIntegerInput(e.target.value) ?? 1)
                )
              }
              type="number"
              value={pickCount}
            />
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={removePicked}
              className="accent-primary"
              onChange={(e) => setRemovePicked(e.target.checked)}
              type="checkbox"
            />
            {strings.removePickedFromPool}
          </label>
        </div>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={activePool.length === 0}
          onClick={handlePick}
          type="button"
        >
          {tPlural(strings.pickNames, locale, pickCount)}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={pool === null && history.length === 0}
          onClick={handleResetPool}
          type="button"
        >
          {strings.resetPool}
        </button>
      </div>

      {/* Result */}
      {lastPicked.length > 0 && (
        <div
          className={`rounded-lg border p-6 text-center transition-colors duration-500 ${highlightClass}`}
        >
          <p className="text-muted-foreground text-sm">
            {strings.pickedAtRandom}
            {lastPicked.length > 1 ? ` (${lastPicked.length})` : ""}
          </p>
          <p className="mt-2 font-semibold text-2xl text-foreground md:text-3xl">
            {lastPicked.join(", ")}
          </p>
        </div>
      )}

      {/* Stats */}
      {history.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label={strings.namesEntered} value={allNames.length} />
          <StatCard label={strings.leftInPool} value={activePool.length} />
          <StatCard label={strings.pickedSoFar} value={history.length} />
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.pickHistory}
          </h2>
          <ol className="list-decimal space-y-1 rounded-lg border border-border bg-card p-5 pl-10 text-foreground text-sm leading-relaxed">
            {history.map((name, index) => (
              <li key={`${name}-${String(index)}`}>{name}</li>
            ))}
          </ol>
        </section>
      )}
    </>
  );
}
